/**
 * Intervention Type Conversion Utilities
 * 
 * This file contains utilities to convert between ActionPlanIntervention (from Catch Backend)
 * and the local Intervention type used by the action plan modal components.
 * 
 * IMPORTANT: This is a temporary solution to bridge the gap between two different
 * data sources and type systems. The long-term goal is to fully migrate to using
 * Catch Backend and Sanity data directly, which should eliminate the need for
 * these type conversions entirely.
 * 
 * TODO: Remove this file once we've completed the migration to a unified data model
 * that doesn't require conversion between ActionPlanIntervention and Intervention types.
 */

import { ActionPlanIntervention } from '@/api/generated-catch-backend/model';
import { 
  Intervention, 
  InterventionCategoryIcon, 
  InterventionStatus as LocalInterventionStatus, 
  InterventionType 
} from '@/app/components/action-plan/types';
import { SourceQuote } from '@/app/components/testing-plan/components/test-details/types';

/**
 * Helper function to extract text from DTOBlock array (multiple blocks and spans possible)
 */
const extractTextFromBlock = (blocks: any): string => {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return '';
  
  // Process all blocks and their spans
  return blocks
    .map(block => {
      if (!block?.children || !Array.isArray(block.children)) return '';
      
      // Join all span texts within a block
      return block.children
        .map((span: any) => span?.text || '')
        .join('');
    })
    .filter(text => text) // Remove empty strings
    .join('\n\n'); // Join blocks with newlines
};

/**
 * Map intervention type string to InterventionType enum
 */
const mapInterventionType = (type: string): InterventionType => {
  const typeMap: Record<string, InterventionType> = {
    'DIET': InterventionType.DIET,
    'LIFESTYLE': InterventionType.LIFESTYLE,
    'OTHER': InterventionType.OTHER,
    'ALL': InterventionType.ALL
  };
  
  // Uppercase the type to ensure case-insensitive matching
  const upperType = type.toUpperCase();
  if (typeMap[upperType]) {
    return typeMap[upperType];
  }
  
  // Default to OTHER if no match found
  return InterventionType.OTHER;
};

/**
 * Map category string to InterventionCategoryIcon enum
 */
const mapCategory = (category: string): InterventionCategoryIcon => {
  // The category values from API should match the enum values
  // Cast as enum, or provide default if not valid
  return category as InterventionCategoryIcon;
};

/**
 * Map sources to SourceQuote array
 */
const mapSources = (sources: any[] | undefined): SourceQuote[] => {
  if (!sources || !Array.isArray(sources)) return [];
  
  return sources.map(source => ({
    description: source.description || '',
    url: source.url || ''
  }));
};

/**
 * Convert ActionPlanIntervention to local Intervention type
 * This is needed to reuse the existing ModalInterventionDetails component
 */
export const convertToLocalIntervention = (action: ActionPlanIntervention): Intervention => {
  // Provide defensive fallbacks for all critical fields
  const title = action.title?.trim() || 'Intervention';
  const whyItMattersShort = action.whyItMattersShortExplanation?.trim() || 'This intervention may help reduce your cancer risk.';
  
  return {
    id: action.id || 'unknown',
    status: action.status as unknown as LocalInterventionStatus,
    interventionType: mapInterventionType(action.interventionType || ''),
    title,
    category: mapCategory(action.category || 'General'),
    whyItMattersShortExplanation: whyItMattersShort,
    whyItMattersFullExplanation: extractTextFromBlock(action.whyItMattersFullExplanation) || whyItMattersShort,
    completedAt: action.completedAt || '',
    riskImpactDescription: action.riskImpactDescription?.trim() || 'May reduce cancer risk',
    copyWhenCompleted: action.copyWhenCompleted?.trim() || 'Great job completing this intervention!',
    riskImpact: action.riskImpact || [],
    overallRiskImpact: action.overallRiskImpact || 0,
    howToAccomplish: extractTextFromBlock(action.howToAccomplish) || 'Consult with your healthcare provider for specific guidance on implementing this intervention.',
    sources: mapSources(action.sources),
    questionId: action.questionId || '',
    assignedAt: action.assignedAt || '',
    isNonQuantifiable: action.isNonQuantifiable || false,
  } as Intervention;
};